import { useState } from "react";
import { Bot } from "lucide-react";
import ManagementAPI from "./managementAPI";

/**
 * LlmStatusCard — super-admin "AI / LLM Status" panel.
 *
 * Calls GET /management/llm-status (proxies nlp_service /health/llm) so a
 * super-admin can verify the Ollama LLM fallback from the UI instead of curl:
 *   • Connectivity test (no org) — is Ollama reachable + generating.
 *   • Stored-KB test (pick an org + question) — does Ollama answer from that
 *     tenant's stored knowledge base (the real production path).
 */
export default function LlmStatusCard({ orgs = [] }) {
    const [status, setStatus]   = useState(null);
    const [loading, setLoading] = useState(false);
    const [orgId, setOrgId]     = useState("");
    const [question, setQuestion] = useState("");

    const run = async (withOrg) => {
        setLoading(true); setStatus(null);
        try {
            const data = await ManagementAPI.getLlmStatus({
                orgId: withOrg ? orgId : undefined,
                q: question.trim() || undefined,
            });
            setStatus(data);
        } catch (e) {
            setStatus({ error: e.message, nlp_reachable: false, reachable: false });
        } finally {
            setLoading(false);
        }
    };

    const Badge = ({ ok, yes = "Yes", no = "No", unknown = "—" }) => (
        <span className={`mg-badge ${ok === true ? "ok" : ok === false ? "danger" : "neutral"}`}>
            {ok === true ? yes : ok === false ? no : unknown}
        </span>
    );

    const answered = status && (status.answered_by_ollama || status.answered);

    return (
        <div className="mg-card mg-card-pad" style={{ marginTop: 16 }}>
            <div className="mg-page-head" style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="mg-icon-box"><Bot size={18} /></div>
                    <div>
                        <h1 className="mg-h1" style={{ fontSize: 18 }}>AI / LLM Status</h1>
                        <p className="mg-sub">Check the Ollama LLM fallback (connectivity, model &amp; a live answer).</p>
                    </div>
                </div>
                <button className="mg-btn mg-btn-primary" onClick={() => run(false)} disabled={loading}>
                    {loading ? "Checking…" : "Check connection"}
                </button>
            </div>

            {/* Stored-KB test row */}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
                <div className="mg-field" style={{ minWidth: 200 }}>
                    <span className="mg-field-label">Test with a tenant's stored KB</span>
                    <select className="mg-select mg-inline" value={orgId} onChange={e => setOrgId(e.target.value)} style={{ width: 220 }}>
                        <option value="">— select organization —</option>
                        {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                </div>
                <div className="mg-field" style={{ flex: 1, minWidth: 220 }}>
                    <span className="mg-field-label">Question (a topic your KB covers)</span>
                    <input className="mg-input mg-inline" style={{ width: "100%" }} placeholder="e.g. how do refunds work"
                        value={question} onChange={e => setQuestion(e.target.value)} />
                </div>
                <button className="mg-btn mg-btn-ghost" onClick={() => run(true)} disabled={loading || !orgId} title="Runs the real stored-KB → Ollama path">
                    Test stored KB
                </button>
            </div>

            {status && (
                <div className="mg-table-wrap">
                    {status.error && (
                        <div className="mg-alert error" style={{ justifyContent: "flex-start", marginBottom: 10 }}>{status.error}</div>
                    )}
                    <table className="mg-table">
                        <tbody>
                            <tr><td className="mg-td-muted">NLP service reachable</td><td><Badge ok={status.nlp_reachable} /></td></tr>
                            <tr><td className="mg-td-muted">Ollama reachable</td><td><Badge ok={status.reachable} /></td></tr>
                            <tr><td className="mg-td-muted">LLM enabled</td><td><Badge ok={status.llm_enabled} /></td></tr>
                            <tr><td className="mg-td-muted">Ollama URL</td><td className="mg-mono">{status.ollama_url || "—"}</td></tr>
                            <tr>
                                <td className="mg-td-muted">Configured model</td>
                                <td>
                                    <span className="mg-mono">{status.configured_model || "—"}</span>{" "}
                                    {status.configured_model_present === false && <span className="mg-badge danger">not on server</span>}
                                    {status.configured_model_present === true && <span className="mg-badge ok">available</span>}
                                </td>
                            </tr>
                            {status.models_available && (
                                <tr><td className="mg-td-muted">Models on server</td><td className="mg-mono">{status.models_available.join(", ") || "—"}</td></tr>
                            )}
                            {status.org_id != null && (
                                <>
                                    <tr>
                                        <td className="mg-td-muted">KB articles retrieved</td>
                                        <td>
                                            {status.kb_retrieved_count > 0
                                                ? (status.kb_articles_retrieved || []).map((a, i) => (
                                                    <span key={i} className="mg-badge info" style={{ marginRight: 4 }}>
                                                        {a.title}{a.relevance != null ? ` (${Number(a.relevance).toFixed(2)})` : ""}
                                                    </span>))
                                                : <span className="mg-badge danger">none — try a topic your KB covers</span>}
                                        </td>
                                    </tr>
                                    <tr><td className="mg-td-muted">Answered by Ollama</td><td><Badge ok={status.answered_by_ollama} /> {status.route ? <span className="mg-td-muted">(route: {status.route})</span> : null}</td></tr>
                                </>
                            )}
                        </tbody>
                    </table>

                    <div style={{ marginTop: 12 }}>
                        <div className="mg-field-label" style={{ marginBottom: 4 }}>
                            Answer {answered ? <span className="mg-badge ok">generated</span> : <span className="mg-badge neutral">none</span>}
                        </div>
                        <div className="mg-card" style={{ padding: 12, whiteSpace: "pre-wrap", fontSize: 13.5 }}>
                            {status.answer || <span className="mg-td-muted">No answer produced. See the badges above for why (reachable / model / KB retrieved).</span>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
